import { useAuth } from '../context/AuthContext';

/** Map sidebar nav key -> SA module_id for permission checks */
export const NAV_TO_MODULE = {
  dashboard: 'dashboard',
  profile: 'profile',
  onboarding: 'onboarding',
  attendance: 'attendance',
  leave: 'attendance',
  calendar: 'calendar',
  policies: 'policies',
  people: 'emp_mgmt',
  helpdesk: 'it_requests',
  assets: 'asset_mgmt',
  timeline: 'attendance',
  announcements: 'announcements',
  chat: 'chat',
  notes: 'notes',
  certificate: 'profile',
  interns: 'intern_mgmt',
  employees: 'emp_mgmt',
  payroll: 'payroll',
  advances: 'salary_adv',
  performance: 'performance',
  tickets: 'it_requests',
  audit: 'action_logs',
  bulk: 'emp_mgmt',
  analytics: 'performance',
  offboarding: 'emp_mgmt',
  settings: 'profile',
  projects: 'proj_mgmt',
  progress: 'proj_mgmt',
  docs: 'proj_docs',
  announce: 'announcements',
  reports: 'performance',
  hrrequests: 'performance',
  permissions: 'attendance',
  notifications: 'announcements',
  offboarding: 'emp_mgmt',
};

/**
 * Returns permission helpers for the current user.
 * - permissions: { chat: 'read', dashboard: 'full', ... }
 * - hasAccess(moduleId, minLevel): boolean - minLevel in ['read','write','full']
 * - canShowNav(navKey): boolean - true if nav item should be visible (not none)
 * - canWrite(moduleId): boolean
 */
export function usePermissions() {
  const { user } = useAuth();
  const perms = user?.permissions || {};

  const hasAccess = (moduleId, minLevel) => {
    if (perms._full) return true;
    if (Object.keys(perms).length === 0) return true;
    const level = perms[moduleId] || 'none';
    const order = { none: 0, read: 1, write: 2, full: 3 };
    return (order[level] || 0) >= (order[minLevel] || 0);
  };

  const canShowNav = (navKey) => {
    // Employee helpdesk is a core self-service flow; do not hide it behind IT module perms.
    if (navKey === 'helpdesk' && String(user?.role || '').toLowerCase() === 'employee') return true;
    // Employee offboarding must be visible for resignation/signature workflow.
    if (navKey === 'offboarding' && String(user?.role || '').toLowerCase() === 'employee') return true;
    // HR Analytics page is a core HR dashboard module; keep visible for HR roles.
    if (navKey === 'analytics' && ['hr', 'superadmin'].includes(String(user?.role || '').toLowerCase())) return true;
    // HR Policies is core publishing flow for employee/intern policy docs.
    if (navKey === 'policies' && ['hr', 'superadmin'].includes(String(user?.role || '').toLowerCase())) return true;
    if (perms._full) return true;
    if (Object.keys(perms).length === 0) return true;
    const mod = NAV_TO_MODULE[navKey];
    if (!mod) return true;
    const level = perms[mod] || 'none';
    return level !== 'none';
  };

  const canWrite = (moduleId) => hasAccess(moduleId, 'write');

  return { permissions: perms, hasAccess, canShowNav, canWrite };
}
